import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressButton from '@/components/common/progress-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { StickerGrid } from '@/components/stickers/sticker-grid';
import useDeleteStickers from '@/hooks/stickers/use-delete-stickers';
import useUserStickers from '@/hooks/stickers/use-user-stickers';
import type { UserSticker } from '@/models/user/custom-sticker';

export default function MyStickersRoute() {
  const { t } = useTranslation();
  const { stickers } = useUserStickers();
  const { mutateAsync: deleteStickers, isPending: isDeleting } = useDeleteStickers();

  const [selectedStickers, setSelectedStickers] = useState<number[]>([]);
  const [isEditing, setEditing] = useState(false);

  const handleSelect = (sticker: UserSticker) => {
    if (!isEditing) return;

    if (selectedStickers.includes(sticker.Id)) {
      setSelectedStickers(selectedStickers.filter((id) => id !== sticker.Id));
    } else {
      setSelectedStickers([...selectedStickers, sticker.Id]);
    }
  };

  const handleDelete = async () => {
    if (selectedStickers.length === 0) return;

    try {
      await deleteStickers(selectedStickers);
      handleReset();
    } catch (error) {
      console.error('Failed to delete stickers:', error);
    }
  };

  const handleReset = () => {
    setEditing(false);
    setSelectedStickers([]);
  };

  return (
    <PageLayout
      title={t('stickers.my-stickers')}
      headerConfig={{
        navigateBack: true,
        actions: !isEditing && (
          <button
            type='button'
            className='btn btn-ghost btn-sm text-inherit'
            onClick={() => setEditing(true)}>
            {t('common.edit')}
          </button>
        ),
      }}
      className='desktop:relative h-full mode-embedded:bg-neutral-100'>
      <div className='flex-1 overflow-auto pb-safe-offset-20'>
        <StickerGrid
          stickers={stickers}
          selectedStickers={isEditing ? selectedStickers : undefined}
          onClick={handleSelect}
          className='desktop:grid-cols-5 desktop:lg:grid-cols-6'
        />
      </div>

      {isEditing && (
        <div className='fixed desktop:absolute bottom-safe-offset-4 desktop:bottom-safe-offset-0 left-0 right-0 mx-5 flex gap-4'>
          <button
            type='button'
            className='flex-1 btn bg-white border border-secondary text-base-content hover:bg-gray-50'
            onClick={handleReset}
            disabled={isDeleting}>
            {t('common.cancel')}
          </button>

          <ProgressButton
            as='button'
            className='flex-1 btn btn-error'
            isLoading={isDeleting}
            disabled={selectedStickers.length === 0}
            onClick={handleDelete}
            label={
              selectedStickers.length > 0 ? `${t('common.delete')}(${selectedStickers.length})` : t('common.delete')
            }
          />
        </div>
      )}
    </PageLayout>
  );
}
